/* What this client sells, and what we still do not know about each thing.

   The ledger is the point, and it always shows ALL FOURTEEN slots. A view
   listing only the answered ones would read as "three things to know about
   this service" — the opposite of the truth, since the value is entirely in
   the gaps.

   `generic` is deliberately not a shade of `covered`. A page can touch every
   slot in competitor boilerplate and know nothing, which is exactly what three
   real service pages did. */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Input } from '@/components/ui';
import { Icon } from '@/lib/icons';
import { useIsAdmin } from '@/auth/useIsAdmin';
import {
  getServices, classifyReviews, checkServiceCoverage, proposeServices, createService, updateService, archiveService, setServiceSlot,
  releaseServiceSlot, recordServiceAsked, askFaqPage, requestPageInterview,
  type ClientService, type ReviewProblem, type CoverageSlot, type CoverageState, type ServiceProposal, type UnclaimedPage,
} from '@/data/client';

const TONE: Record<CoverageState, { bg: string; label: string }> = {
  covered: { bg: 'var(--ok, #15803d)', label: 'On record' },
  generic: { bg: 'var(--warn, #b45309)', label: 'Only in generic terms' },
  open: { bg: 'var(--border, #d1d5db)', label: 'Nobody has answered this' },
};

/* Cycles rather than offering a menu: there are three states and the common
   action is "that is wrong, it is actually open". */
const NEXT: Record<CoverageState, CoverageState> = { open: 'covered', covered: 'generic', generic: 'open' };

const STATUS_WORD: Record<string, string> = { planned: 'planned', scaffolded: 'scaffolded', built: 'built', live: 'live', retired: 'retired' };

function SlotRow({ slot, onSet, onRelease, busy, picked, onPick, canAsk }: {
  slot: CoverageSlot; onSet: (s: CoverageState) => void; onRelease: () => void; busy: boolean;
  /* An open or generic slot can be ticked and sent as a question. */
  picked: boolean; onPick: () => void; canAsk: boolean;
}) {
  const askable = slot.state !== 'covered';
  return (
    <li style={{ display: 'grid', gridTemplateColumns: '16px 12px 1fr', gap: 8, alignItems: 'start', margin: '4px 0' }}>
      {askable && canAsk
        ? <input type="checkbox" checked={picked} onChange={onPick} disabled={busy} style={{ margin: '3px 0 0' }} title="Ask the client this" />
        : <span />}
      <button
        type="button"
        title={`${TONE[slot.state].label} — click to change`}
        disabled={busy}
        onClick={() => onSet(NEXT[slot.state])}
        style={{
          width: 10, height: 10, borderRadius: 3, marginTop: 5, padding: 0,
          border: 'none', cursor: busy ? 'default' : 'pointer', background: TONE[slot.state].bg,
        }}
      />
      <span style={{ fontSize: 13 }}>
        <span style={{ opacity: slot.state === 'open' ? 0.65 : 1 }}>{slot.label}</span>
        {slot.note && <span className="dash-muted" style={{ fontSize: 12 }}> — {slot.note}</span>}
        {/* A hand-set slot survives every audit. Saying so, with the way out,
            is what keeps "why did the audit not change this" from being a
            support question. */}
        {slot.pinned && (
          <span className="dash-muted" style={{ fontSize: 11 }}>
            {' '}· set by hand{' '}
            <button type="button" disabled={busy} onClick={onRelease}
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--triton-blue)', fontSize: 11 }}>
              let the audit decide
            </button>
          </span>
        )}
      </span>
    </li>
  );
}

function ServiceCard({ s, busy, checking, crawled, registry, onSlot, onRelease, onCheck, onArchive, onSent }: {
  s: ClientService; busy: boolean; checking: boolean;
  /* Routes the Vault has the text of. A route the Vault has never crawled is
     a name in a sitemap, not a page anything can read. */
  crawled: Set<string>;
  registry: boolean;
  onSlot: (slot: string, state: CoverageState) => void;
  onRelease: (slot: string) => void;
  onCheck: () => void;
  onArchive: () => void;
  onSent: (msg: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [sending, setSending] = useState<'' | 'faq' | 'interview'>('');
  const [askErr, setAskErr] = useState('');
  const total = s.slots.length;
  const gaps = s.slots.filter((x) => x.state !== 'covered').length;
  /* Questions go through the service's first page: that is the page the
     answers come back to as FAQs, and the ledger row that the Vault check
     closes. No page, nothing to ask through. */
  const askRoute = s.routes[0] ?? '';
  const pick = (key: string) => setPicked((set) => {
    const n = new Set(set);
    if (n.has(key)) n.delete(key); else n.add(key);
    return n;
  });
  const send = async (how: 'faq' | 'interview') => {
    const slots = s.slots.filter((x) => picked.has(x.key) && x.state !== 'covered');
    if (!slots.length || !askRoute) return;
    setSending(how); setAskErr('');
    try {
      if (how === 'faq') {
        const r = await askFaqPage(askRoute, slots.map((x) => x.q));
        onSent(`Sent ${r.sent} question${r.sent === 1 ? '' : 's'} about ${s.name} to the client${r.drafted ? `, ${r.drafted} with a draft answer from the Vault` : ''}.`);
      } else {
        const r = await requestPageInterview({ page: askRoute, title: `Interview for ${s.name}`, questions: slots.map((x) => ({ q: x.q, route: x.routeTo })) });
        onSent(`Interview requested — "${r.title}" is on the client's dashboard.`);
      }
      /* Recorded after the send, so a failed send leaves no row claiming the
         client was asked. */
      await recordServiceAsked(s.id, slots.map((x) => x.key), how);
      setPicked(new Set());
    } catch (e) { setAskErr(e instanceof Error ? e.message : 'Could not send those.'); }
    finally { setSending(''); }
  };

  return (
    <div style={{ borderTop: '1px solid var(--border, #e5e7eb)', padding: '10px 0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center' }}>
        <button type="button" onClick={() => setOpen((v) => !v)}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left', flex: 1 }}>
          <strong style={{ fontSize: 14 }}>{s.name}</strong>
          <span className="dash-muted" style={{ fontSize: 13 }}> · {s.covered}/{total} on record</span>
          <span className="dash-muted" style={{ fontSize: 12 }}>
            {' '}· {s.routes.length
              ? `${s.routes.length} page${s.routes.length === 1 ? '' : 's'}`
              : 'no pages matched'}
            {!s.checkedAt && ' · never audited'}
            {s.checkedAt && s.stale && ' · Vault has new material, re-audit due'}
          </span>
        </button>
        <Button variant="outline" size="sm" disabled={busy || checking} onClick={onCheck}>
          {checking ? 'Reading…' : s.checkedAt ? 'Re-audit now' : 'Audit the Vault'}
        </Button>
        <Button variant="ghost" size="sm" disabled={busy} onClick={onArchive}>Remove</Button>
      </div>

      {/* The bar is the ledger at a glance: mostly grey means mostly unknown. */}
      <div style={{ display: 'flex', gap: 2, margin: '6px 0 0' }}>
        {s.slots.map((x) => (
          <span key={x.key} title={`${x.label} — ${TONE[x.state].label}`}
            style={{ flex: 1, height: 4, borderRadius: 2, background: TONE[x.state].bg }} />
        ))}
      </div>

      {open && (
        <>
        {/* The pages themselves, not a count — a service whose routes did not
            match is the common setup mistake, and a number hides it. */}
        <div style={{ padding: '8px 0 0' }}>
          {s.routes.length === 0
            ? (
              <p className="dash-muted" style={{ fontSize: 12, margin: 0 }}>
                No pages matched this service. Add them by hand, or re-run the suggestion once a
                sitemap is in the Vault.
              </p>
            )
            : s.pages.map((p) => (
              <div key={p.route} style={{ fontSize: 12 }} className="dash-muted">
                {/* The page's own name from the site map, with the route beside
                    it, and its status: a service whose only page is still
                    planned is a service with nowhere for answers to go. */}
                {p.title ? <><strong style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{p.title}</strong> · </> : null}
                <code style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{p.route}</code>
                {registry && !p.onSiteMap && <span style={{ color: 'var(--warn, #b45309)' }}> · not on the site map</span>}
                {p.status && p.status !== 'live' && p.status !== 'built' && (
                  <span style={{ color: 'var(--warn, #b45309)' }}> · {STATUS_WORD[p.status] ?? p.status}, not written yet</span>
                )}
                {crawled.has(p.route)
                  ? <span style={{ color: 'var(--ok, #15803d)' }}> · in the Vault</span>
                  : (p.status === 'live' || p.status === 'built' || !p.status) && <span style={{ color: 'var(--warn, #b45309)' }}> · not crawled yet</span>}
              </div>
            ))}
          <p className="dash-muted" style={{ fontSize: 12, margin: '6px 0 0' }}>
            {s.checkedAt
              ? `Audited ${s.checkedAt.slice(0, 10)}. The audit re-runs on its own when the Vault changes.`
              : 'Not audited yet. It runs on its own once the Vault has material, or press the button.'}
          </p>
        </div>
        <ul style={{ listStyle: 'none', padding: '8px 0 0', margin: 0 }}>
          {s.slots.map((x) => (
            <SlotRow key={x.key} slot={x} busy={busy} canAsk={!!askRoute}
              onSet={(state) => onSlot(x.key, state)} onRelease={() => onRelease(x.key)}
              picked={picked.has(x.key)} onPick={() => pick(x.key)} />
          ))}
          <li className="dash-muted" style={{ fontSize: 12, marginTop: 6 }}>
            {gaps} of {total} still to ask about. Click a dot to correct it
            {askRoute ? '; tick the ones to ask the client.' : '. Attach a page to this service to ask the client from here.'}
          </li>
        </ul>
        {/* The way out of a gap: the ticked slots go to the client as one
            request, phrased for this service, through its page. Nothing on
            this screen writes content; answers come back through the Vault
            and the slot flips when the check finds them. */}
        {askRoute && picked.size > 0 && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 8 }}>
            <Button variant="primary" size="sm" disabled={!!sending || busy} onClick={() => send('interview')}>
              {sending === 'interview' ? 'Requesting…' : `Request an interview (${picked.size})`}
            </Button>
            <Button variant="outline" size="sm" disabled={!!sending || busy} onClick={() => send('faq')}>
              {sending === 'faq' ? 'Sending…' : `Ask by email (${picked.size})`}
            </Button>
            <span className="dash-muted" style={{ fontSize: 12 }}>Through {askRoute}; answers come back as page FAQs and Vault material.</span>
          </div>
        )}
        {askErr && <p className="dash-muted" style={{ color: 'var(--danger, #b91c1c)', fontSize: 13 }}>{askErr}</p>}
        </>
      )}
    </div>
  );
}

/* One unclaimed page: make it a service of its own, or attach it to one. */
function UnclaimedRow({ p, services, busy, onCreate, onAttach }: {
  p: UnclaimedPage; services: ClientService[]; busy: boolean;
  onCreate: () => void; onAttach: (svc: ClientService) => void;
}) {
  const [to, setTo] = useState('');
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', padding: '4px 0', fontSize: 13 }}>
      <span style={{ flex: '1 1 220px', minWidth: 0 }}>
        <strong>{p.title || p.route}</strong>
        {' '}<code style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }} className="dash-muted">{p.route}</code>
        {p.status !== 'live' && p.status !== 'built' && <span className="dash-muted" style={{ fontSize: 12 }}> · {p.status}</span>}
      </span>
      <Button variant="outline" size="sm" disabled={busy} onClick={onCreate}>Create a service</Button>
      {services.length > 0 && (
        <>
          <select value={to} onChange={(e) => setTo(e.target.value)} style={{ fontSize: 12 }}>
            <option value="">Attach to…</option>
            {services.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <Button variant="ghost" size="sm" disabled={busy || !to}
            onClick={() => { const svc = services.find((s) => s.id === to); if (svc) { onAttach(svc); setTo(''); } }}>
            Attach
          </Button>
        </>
      )}
    </div>
  );
}

/* One heading per service, with the unattributed ones last. A review that
   never said what the work was still carries a real symptom — it just has
   nowhere to be put, which is worth seeing rather than hiding. */
function groupProblems(items: ReviewProblem[]): [string, ReviewProblem[]][] {
  const by = new Map<string, ReviewProblem[]>();
  for (const it of items) {
    for (const name of it.services.length ? it.services : ['Service unclear']) {
      by.set(name, [...(by.get(name) ?? []), it]);
    }
  }
  return [...by.entries()].sort(
    (a, b) => (a[0] === 'Service unclear' ? 1 : 0) - (b[0] === 'Service unclear' ? 1 : 0)
      || b[1].length - a[1].length,
  );
}

export function Services() {
  const isAdmin = useIsAdmin();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['services'], queryFn: getServices, enabled: isAdmin });
  /* Collapsed once the list exists. Twenty services is a screenful, and this
     panel is reference material most of the time — the number worth seeing
     without opening it is how much is still unknown. Setup is the exception,
     so an empty list opens itself. */
  const [open, setOpen] = useState(false);
  const [proposals, setProposals] = useState<ServiceProposal[] | null>(null);
  const [pagesRead, setPagesRead] = useState(0);
  const [adding, setAdding] = useState('');
  const [err, setErr] = useState('');

  const done = () => { qc.invalidateQueries({ queryKey: ['services'] }); setErr(''); };
  const fail = (e: unknown) => setErr(e instanceof Error ? e.message : 'Failed');

  const propose = useMutation({
    mutationFn: proposeServices,
    onSuccess: (r) => { setProposals(r.proposals); setPagesRead(r.pagesRead); setErr(''); },
    onError: fail,
  });
  const create = useMutation({
    mutationFn: (b: { name: string; routes?: string[] }) => createService(b), onSuccess: done, onError: fail,
  });
  const archive = useMutation({ mutationFn: archiveService, onSuccess: done, onError: fail });
  const slot = useMutation({
    mutationFn: (a: { id: string; slot: string; state: CoverageState }) => setServiceSlot(a.id, a.slot, a.state),
    onSuccess: done, onError: fail,
  });
  const release = useMutation({
    mutationFn: (a: { id: string; slot: string }) => releaseServiceSlot(a.id, a.slot),
    onSuccess: done, onError: fail,
  });
  /* Attaching keeps everything the service already claims; the page is added
     to its list. */
  const attach = useMutation({
    mutationFn: (a: { service: ClientService; route: string }) => updateService(a.service.id, { name: a.service.name, routes: [...a.service.routes, a.route], sort: a.service.sort }),
    onSuccess: done, onError: fail,
  });
  const busy = create.isPending || archive.isPending || slot.isPending || release.isPending || attach.isPending;
  const services = data?.services ?? [];
  const unclaimed = data?.unclaimed ?? [];
  const [sentMsg, setSentMsg] = useState('');

  /* One call per service, run in sequence with the progress visible. A single
     button that silently spends twenty model calls is a button people stop
     pressing. */
  const [checking, setChecking] = useState('');
  const [tagging, setTagging] = useState(false);
  const [tagMsg, setTagMsg] = useState('');
  const [doneMsg, setDone] = useState('');
  const [problems, setProblems] = useState<ReviewProblem[]>([]);

  /* Batched with the count visible, like every other long job here. */
  const tagReviews = async () => {
    setTagging(true); setErr(''); setDone(''); setProblems([]);
    try {
      const found: ReviewProblem[] = [];
      for (let i = 0; i < 20; i += 1) {
        const r = await classifyReviews();
        /* Guarded as well as fixed server-side. A caller that trusts a
           response shape is one deploy away from this again. */
        found.push(...(r.problems ?? []));
        setTagMsg(r.remaining ? `${r.remaining} reviews left…` : '');
        /* "Every review is already tagged" is the normal second click, not a
           failure. Showing it in red taught people the button was broken. */
        if (r.note && !r.classified) { setDone(r.note); break; }
        if (!r.remaining) break;
      }
      /* De-duplicated on the words: two customers describing the same symptom
         is one thing to write about, not two. */
      const byText = new Map(found.map((f) => [f.problem.toLowerCase(), f]));
      setProblems([...byText.values()]);
    } catch (e) { setErr(e instanceof Error ? e.message : 'Could not tag those.'); }
    finally { setTagging(false); setTagMsg(''); }
  };
  const [progress, setProgress] = useState('');
  const check = async (id: string) => {
    setChecking(id); setErr('');
    try { await checkServiceCoverage(id); await qc.invalidateQueries({ queryKey: ['services'] }); }
    catch (e) { fail(e); } finally { setChecking(''); }
  };
  const checkAll = async () => {
    const todo = services.filter((s) => !s.checkedAt);
    for (let i = 0; i < todo.length; i += 1) {
      setProgress(`${i + 1} of ${todo.length}`);
      // eslint-disable-next-line no-await-in-loop
      await check(todo[i].id);
    }
    setProgress('');
  };

  if (!isAdmin) return null;
  const crawledRoutes = new Set(data?.crawledRoutes ?? []);
  const slots = services.reduce((n, s) => n + s.slots.length, 0);
  const covered = services.reduce((n, s) => n + s.covered, 0);
  const untouched = services.filter((s) => s.covered === 0).length;
  /* Collapsed from the start. Proposals are the one exception: they are an
     unsaved conversation, and shutting the panel underneath one would throw
     the list away. */
  const expanded = open || !!proposals;

  const accept = async (p: ServiceProposal) => {
    await create.mutateAsync({ name: p.name, routes: p.routes });
    setProposals((ps) => (ps ? ps.filter((x) => x.name !== p.name) : ps));
  };

  return (
    <Card padding={false} className="dash-leads">
      <div className="dash-panelhead dash-panelhead--pad">
        <button type="button" onClick={() => setOpen((v) => !v)}
          style={{ background: 'none', border: 'none', padding: 0, textAlign: 'left', cursor: 'pointer', flex: 1 }}>
          <h2>
            <Icon name={expanded ? 'chevron-down' : 'chevron-right'} size={14} />
            {' '}<Icon name="list" size={16} /> Services
            {services.length > 0 && (
              <span className="dash-muted" style={{ fontWeight: 400, fontSize: 13 }}>
                {' '}· {services.length} · {covered}/{slots} on record
              </span>
            )}
          </h2>
          <p>
            {services.length === 0
              ? 'What this client sells, and which of the things a buyer asks are still unanswered for each.'
              : untouched > 0
                ? `${untouched} with nothing on record yet.`
                : 'Every service has something on record.'}
          </p>
        </button>
        {/* Only once services exist: there is nothing to tag reviews against
            before that, which is also what the route says if pressed. */}
        {expanded && services.length > 0 && (
          <Button variant="outline" size="sm" disabled={tagging} onClick={tagReviews}>
            {tagging ? (tagMsg || 'Reading reviews…') : 'Tag reviews by service'}
          </Button>
        )}
        {expanded && services.some((s) => !s.checkedAt) && (
          <Button variant="outline" size="sm" disabled={!!checking}
            onClick={checkAll}>
            {progress ? `Reading ${progress}…` : `Check ${services.filter((s) => !s.checkedAt).length} against the Vault`}
          </Button>
        )}
        {expanded && (
          <Button variant="outline" size="sm" disabled={propose.isPending}
            iconLeft={<Icon name="sparkles" size={15} />} onClick={() => propose.mutate()}>
            {propose.isPending ? 'Reading their site…' : 'Suggest from their site'}
          </Button>
        )}
      </div>

      {expanded && (
      <div style={{ padding: '0 16px 16px' }}>
        {err && <p className="dash-muted" style={{ color: 'var(--danger, #b91c1c)' }}>{err}</p>}

        {proposals && (
          <div style={{ padding: '8px 0' }}>
            <p className="dash-muted" style={{ fontSize: 12, margin: '0 0 6px' }}>
              Read {pagesRead} page{pagesRead === 1 ? '' : 's'}. Nothing is saved until you add it —
              a wrong service here follows into every question set afterwards.
            </p>
            {proposals.length === 0
              ? <p className="dash-muted" style={{ fontSize: 13 }}>Nothing suggested. Is a sitemap in the Vault?</p>
              : proposals.map((p) => (
                <div key={p.name} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '4px 0' }}>
                  <Button variant="primary" size="sm" disabled={busy || p.existing} onClick={() => accept(p)}>
                    {p.existing ? 'Already added' : 'Add'}
                  </Button>
                  <span style={{ fontSize: 13 }}>
                    <strong>{p.name}</strong>
                    {p.why && <span className="dash-muted"> — {p.why}</span>}
                    {p.routes.length > 0 && (
                      <span className="dash-muted" style={{ fontSize: 12 }}> · {p.routes.join(', ')}</span>
                    )}
                  </span>
                </div>
              ))}
            <Button variant="ghost" size="sm" onClick={() => setProposals(null)}>Done</Button>
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', padding: '8px 0' }}>
          <div style={{ flex: '1 1 240px' }}>
            <Input label="Add a service" placeholder="e.g. Panel upgrades" value={adding}
              onChange={(e) => setAdding(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && adding.trim()) { create.mutate({ name: adding.trim() }); setAdding(''); } }} />
          </div>
          <Button variant="outline" size="sm" disabled={busy || !adding.trim()}
            onClick={() => { create.mutate({ name: adding.trim() }); setAdding(''); }}>Add</Button>
        </div>

        {/* The demand signal, and the reason this is worth more than the
            tagging. For an established business with no Search Console yet,
            what customers arrived worried about — in their own words — is the
            only first-party intent data in existence. Shown rather than
            stored: it informs what to write about, and keeping it would be a
            second copy of review text. */}
        {doneMsg && <p className="dash-muted" style={{ fontSize: 13 }}>{doneMsg}</p>}

        {problems.length > 0 && (
          <div style={{ padding: '8px 0' }}>
            <p style={{ fontWeight: 600, fontSize: 13, margin: '0 0 4px' }}>
              What customers arrived with ({problems.length})
            </p>
            <p className="dash-muted" style={{ fontSize: 12, margin: '0 0 6px' }}>
              Their words, not ours. These are page sections, FAQs and the phrases people search.
            </p>
            {/* Grouped by service, because a problem is only actionable once
                somebody knows where it goes. Ones the review never pinned to a
                service come last: still worth reading, nowhere to put them. */}
            {groupProblems(problems).map(([service, items]) => (
              <div key={service} style={{ margin: '8px 0 0' }}>
                <p style={{ fontSize: 13, fontWeight: 600, margin: 0 }}>
                  {service}
                  {items[0]?.routes.length > 0 && (
                    <span className="dash-muted" style={{ fontWeight: 400, fontSize: 12 }}>
                      {' '}· {items[0].routes.join(', ')}
                    </span>
                  )}
                </p>
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {items.map((pb, i) => (
                    <li key={i} className="dash-muted" style={{ fontSize: 13, margin: '3px 0' }}>&ldquo;{pb.problem}&rdquo;</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}

        {isLoading && <p className="dash-muted">Loading…</p>}
        {!isLoading && services.length === 0 && (
          <p className="dash-muted" style={{ fontSize: 13 }}>
            No services yet. Until there are, nothing can tell you which questions this client has never been asked.
          </p>
        )}

        {/* Service pages on the site map that no service claims. The audit
            reaches a page only through a service, so these are pages nobody
            will ever be asked about until somebody says which service they
            belong to. */}
        {unclaimed.length > 0 && (
          <div style={{ padding: '8px 0', borderTop: '1px solid var(--border, #e5e7eb)' }}>
            <p style={{ fontWeight: 600, fontSize: 13, margin: '0 0 2px' }}>
              Service pages with no service ({unclaimed.length})
            </p>
            <p className="dash-muted" style={{ fontSize: 12, margin: '0 0 6px' }}>
              On the site map as service pages, but no service here claims them, so nothing audits them.
            </p>
            {unclaimed.map((p) => (
              <UnclaimedRow key={p.route} p={p} services={services} busy={busy}
                onCreate={() => create.mutate({ name: p.title || p.route, routes: [p.route] })}
                onAttach={(svc) => attach.mutate({ service: svc, route: p.route })} />
            ))}
          </div>
        )}

        {sentMsg && <p style={{ color: 'var(--ok, #15803d)', fontSize: 13 }}>{sentMsg}</p>}

        {services.map((s) => (
          <ServiceCard key={s.id} s={s} busy={busy} crawled={crawledRoutes} registry={!!data?.registry}
            checking={checking === s.id}
            onSlot={(sl, st) => slot.mutate({ id: s.id, slot: sl, state: st })}
            onRelease={(sl) => release.mutate({ id: s.id, slot: sl })}
            onCheck={() => check(s.id)}
            onArchive={() => archive.mutate(s.id)}
            onSent={(m) => { setSentMsg(m); setErr(''); }} />
        ))}
      </div>
      )}
    </Card>
  );
}
